import { Box } from '@mui/material'
import TablePaginationActions from '@mui/material/TablePagination/TablePaginationActions'
import type {
  GridCallbackDetails,
  GridColDef,
  GridColumnHeaderParams,
  GridSortModel,
} from '@mui/x-data-grid'
import { DataGrid, GridToolbar } from '@mui/x-data-grid'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import type { FotaDeviceHistory } from '@/api/fota'
import { useFetchFotaHistoryPage } from '@/api/fota'
import useDebounce from '@/hooks/Debounce'
import { DateTime } from '@/pages/utils/formatter'

const FotaHistory: React.FC = () => {
  const { uid } = useParams<{ uid: string }>()
  const { t } = useTranslation(['app', 'common'])

  const { fetchHistoryPage } = useFetchFotaHistoryPage()

  // state
  const [rows, setRows] = useState<FotaDeviceHistory[]>([])
  const [rowCount, setRowCount] = useState<number>(0)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [pageIndex, setPageIndex] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const [searchTerm, setSearchTerm] = useState('')
  const debouncedSearchTerm = useDebounce(searchTerm, 300)
  const [sortModel, setSortModel] = useState<GridSortModel>([])

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newSearchTerm = event.target.value
      setSearchTerm(newSearchTerm)
      setPageIndex(0)
    },
    [],
  )

  const handleSortModelChange = useCallback(
    (newSortModel: GridSortModel, _details: GridCallbackDetails) => {
      setSortModel(newSortModel)
      setPageIndex(0)
    },
    [],
  )

  const handlePaginationModelChange = useCallback(
    (params: { page: number; pageSize: number }) => {
      setPageSize(params.pageSize)
      setPageIndex(params.page)
    },
    [],
  )

  useEffect(() => {
    const fetchData = async () => {
      if (!uid) return
      setIsLoading(true)
      try {
        const sortBy =
          sortModel.length > 0
            ? [
                {
                  id: sortModel[0].field,
                  desc: sortModel[0].sort === 'desc',
                },
              ]
            : []

        const result = await fetchHistoryPage(uid, {
          pageIndex,
          pageSize,
          sortBy,
          globalFilter: debouncedSearchTerm,
        })

        const transformed = result.data?.map((report) => ({
          ...report,
          id: report.updatedTime,
        }))

        setRows(transformed ?? [])
        setRowCount(result.rowCount ?? 0)
      } catch (err: unknown) {
        console.error(err)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, pageIndex, pageSize, debouncedSearchTerm, JSON.stringify(sortModel)])

  const renderWrappedHeader = (params: GridColumnHeaderParams) => (
    <div
      style={{
        whiteSpace: 'normal',
        lineHeight: '1.5',
        fontWeight: 600,
        wordBreak: 'break-word',
        padding: '8px 0',
      }}
    >
      {params.colDef.headerName}
    </div>
  )

  // columns
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'updatedTime',
        headerName: t('device.fotaDeviceInfo.updatedDate'),
        renderHeader: renderWrappedHeader,
        flex: 1,
        minWidth: 100,
        sortable: true,
        renderCell: (params) => <DateTime value={params.value} />,
      },
      {
        field: 'updatedBy',
        headerName: t('device.fotaDeviceInfo.updatedBy'),
        renderHeader: renderWrappedHeader,
        flex: 1,
        minWidth: 100,
        sortable: true,
      },
      {
        field: 'firmwareType',
        headerName: t('device.fotaDeviceInfo.firmwareType'),
        renderHeader: renderWrappedHeader,
        flex: 1,
        minWidth: 100,
        sortable: true,
      },
      {
        field: 'previousFirmwareVersion',
        headerName: t('device.fotaDeviceInfo.previousVersion'),
        renderHeader: renderWrappedHeader,
        flex: 1,
        minWidth: 100,
        sortable: true,
      },
      {
        field: 'currentFirmwareVersion',
        headerName: t('device.fotaDeviceInfo.currentVersion'),
        renderHeader: renderWrappedHeader,
        flex: 1,
        minWidth: 100,
        sortable: true,
      },
      {
        field: 'status',
        headerName: t('device.fotaDeviceInfo.status'),
        renderHeader: renderWrappedHeader,
        flex: 1,
        minWidth: 100,
        sortable: true,
      },
    ],
    [t],
  )

  // toolbar config (quick filter = search)
  const toolbarSlotProps = useMemo(
    () => ({
      showQuickFilter: true,
      quickFilterProps: {
        value: searchTerm,
        onChange: handleSearchChange,
      },
      csvOptions: { disableToolbarButton: true },
      printOptions: { disableToolbarButton: true },
    }),
    [searchTerm, handleSearchChange],
  )

  const paginationSlotProps = useMemo(
    () => ({
      showFirstButton: true,
      showLastButton: true,
      ActionsComponent: TablePaginationActions,
    }),
    [],
  )

  if (!uid) return <></>

  return (
    <Box
      sx={{
        borderRadius: '4px',
        overflow: 'auto',
        width: '100%',
        '& .MuiDataGrid-cell': {
          whiteSpace: 'normal',
          lineHeight: '1.5',
        },
      }}
    >
      <DataGrid
        data-testid="fota-grid"
        rowHeight={50}
        rows={rows}
        getRowId={(row) => row.id}
        columns={columns}
        autoHeight // DO NOT REMOVE - Even though 'autoHeight' is deprecated, the other alternatives do not fix the scrolling issue.
        disableVirtualization
        paginationMode="server"
        sortingMode="server"
        filterMode="server"
        hideFooterSelectedRowCount
        pageSizeOptions={[5, 10, 25, 50, 100]}
        loading={isLoading}
        rowCount={rowCount}
        paginationModel={{ page: pageIndex, pageSize }}
        onPaginationModelChange={handlePaginationModelChange}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        slots={{ toolbar: GridToolbar }}
        slotProps={{
          toolbar: toolbarSlotProps,
          pagination: paginationSlotProps,
        }}
        disableColumnFilter={true}
        disableColumnMenu={true}
        disableDensitySelector={true}
        disableColumnSelector={true}
        localeText={{ noRowsLabel: t('common:table.noRecordsFound') }}
      />
    </Box>
  )
}

export default FotaHistory
